// Google Analytics 4 (gtag.js). Loads into <head> once, only after the visitor
// allows analytics cookies. Page views are sent manually on every route change
// because this is a single-page app.
import { supabase } from "@/integrations/supabase/client";
import { analyticsAllowed } from "@/lib/analytics";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let measurementId: string | null = null;
let loading: Promise<string | null> | null = null;

async function loadGA4(): Promise<string | null> {
  if (measurementId) return measurementId;
  if (loading) return loading;
  loading = (async () => {
    try {
      const { data } = await supabase.functions.invoke("ga-config", { method: "GET" });
      const id = (data as { measurementId?: string | null })?.measurementId;
      if (!id) return null;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params -- gtag.js needs the Arguments object
        window.dataLayer.push(arguments);
      };
      window.gtag("set", "developer_id.dZjgwMW", true);
      window.gtag("js", new Date());
      window.gtag("config", id, { send_page_view: false });
      const sid = sessionStorage.getItem("wekit_visitor_session_id");
      if (sid) window.gtag("set", { wekit_session_id: sid });
      measurementId = id;
      return id;
    } catch {
      return null;
    } finally {
      loading = null;
    }
  })();
  return loading;
}

export async function ga4PageView(path: string) {
  if (!analyticsAllowed()) return;
  const id = await loadGA4();
  if (!id || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export async function ga4Event(name: string, params: Record<string, unknown> = {}) {
  if (!analyticsAllowed()) return;
  const id = await loadGA4();
  if (!id || !window.gtag) return;
  window.gtag("event", name, params);
}

const ITEM = { item_id: "WCC360", item_name: "WeKIT Career Clarity 360", quantity: 1 };

export function ga4BeginCheckout(value: number, currency: string, provider: string) {
  void ga4Event("begin_checkout", {
    currency, value, payment_type: provider, items: [{ ...ITEM, price: value }],
  });
}

/** Fires once per transaction, even across refreshes. */
export function ga4Purchase(transactionId: string, value: number, currency: string) {
  const key = `ga4_purchase_${transactionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch { /* storage blocked: still send once per page */ }
  void ga4Event("purchase", {
    transaction_id: transactionId, value, currency, items: [{ ...ITEM, price: value }],
  });
}

/** Fires a custom event at most once per browser session. */
export function ga4Once(name: string, params: Record<string, unknown> = {}) {
  const key = `ga4_once_${name}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch { /* ignore */ }
  void ga4Event(name, params);
}

if (typeof window !== "undefined") {
  window.addEventListener("wekit-analytics-on", () => {
    void ga4PageView(window.location.pathname + window.location.search);
  });
}
